import { useState, useEffect, useMemo } from "react";
import api from "../lib/api";
import DateFilter, { resolveRange } from "../components/DateFilter";
import { SectionCard } from "../components/shared";
import { inr } from "../lib/format";
import { FileBarChart } from "lucide-react";

const Row = ({ l, v }) => (
  <div className="flex items-center justify-between py-2 border-b border-border/60 last:border-0">
    <span className="text-sm text-brand-slate">{l}</span><span className="font-semibold text-navy tabular-nums">{v}</span>
  </div>
);

export default function Reports() {
  const [preset, setPreset] = useState("This Month");
  const [cf, setCf] = useState(""); const [ct, setCt] = useState("");
  const range = useMemo(() => resolveRange(preset, cf, ct), [preset, cf, ct]);
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/reports", { params: { date_from: range.from, date_to: range.to } }).then((r) => setData(r.data)); }, [range.from, range.to]);

  const p = data?.procurement || {}; const perf = data?.performance || {}; const s = data?.stock || {};
  return (
    <div className="space-y-6 animate-fade-up">
      <div><h1 className="font-heading text-3xl font-bold text-navy tracking-tight flex items-center gap-2"><FileBarChart className="w-7 h-7 text-brand-teal" /> Reports</h1>
        <p className="text-brand-slate text-sm mt-1">Weekly / monthly procurement, timeliness & stock summary</p></div>
      <div className="bg-white border border-border rounded-xl p-4"><DateFilter value={preset} onChange={setPreset} customFrom={cf} customTo={ct} setCustomFrom={setCf} setCustomTo={setCt} /></div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <SectionCard title="Procurement Activity">
          <Row l="Requirements Received" v={p.requirements_received ?? 0} />
          <Row l="Requirements Completed" v={p.requirements_completed ?? 0} />
          <Row l="Quotations Requested" v={p.quotations_requested ?? 0} />
          <Row l="Quotations Received" v={p.quotations_received ?? 0} />
          <Row l="Purchases Completed" v={p.purchases_completed ?? 0} />
          <Row l="Purchase Value" v={inr(p.purchase_value || 0)} />
          <Row l="Vendors Used" v={p.vendors_used ?? 0} />
          <Row l="Materials Received" v={p.materials_received ?? 0} />
        </SectionCard>
        <SectionCard title="Requirement Performance">
          <Row l="On Time" v={perf.on_time ?? 0} />
          <Row l="Completed Early" v={perf.early ?? 0} />
          <Row l="Overdue" v={perf.overdue ?? 0} />
          <Row l="On Hold" v={perf.hold ?? 0} />
          <Row l="Pending" v={perf.pending ?? 0} />
          <div className="mt-3 bg-navy rounded-lg p-4 flex items-center justify-between">
            <span className="text-white text-sm font-semibold">On-Time Fulfilment</span>
            <span className="font-heading text-2xl font-bold text-brand-emerald" data-testid="report-ontime">{perf.on_time_pct == null ? "—" : `${Math.round(perf.on_time_pct * 100)}%`}</span>
          </div>
        </SectionCard>
        <SectionCard title="Stock">
          <Row l="Total Stock Items" v={s.total_items ?? 0} />
          <Row l="Items Received (In)" v={s.items_received ?? 0} />
          <Row l="Items Issued (Out)" v={s.items_issued ?? 0} />
          <Row l="Low Stock" v={s.low_stock ?? 0} />
          <Row l="Expiring Soon" v={s.expiring_soon ?? 0} />
          <Row l="Expired" v={s.expired ?? 0} />
        </SectionCard>
      </div>
    </div>
  );
}
